/**
 * Module: screens/onboarding/steps/PreparingStep
 * Role: Buyer onboarding Step 3 — 4-step fake-loader while PATCH /api/v2/buyer/profile
 *       persists city + marketplaces + onboarding_completed=true. On success navigates
 *       to home. On error shows retry CTA (fail-closed, L-245).
 *       ADR-023: no window.Telegram access, no platform literals.
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - @tanstack/react-query:useQueryClient
 *   - providers/PlatformProvider:usePlatform
 *   - api/client:apiFetch
 *   - screens/onboarding/store:useOnboardingStore
 *   - screens/flow/atoms/Caveat
 *   - screens/flow/atoms/Icons:Check
 *   - styles/tokens:P, T
 *
 * Used by: screens/onboarding/BuyerOnboardingScreen.tsx (route path="preparing")
 * Glossary: ai/glossary/buyer.md
 */

// ADR-023: no window.Telegram / platform literals outside src/platform/
// §7: no openExternal(BOT_LINK)

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePlatform } from '../../../providers/PlatformProvider';
import { apiFetch, setOnboardingCompleted } from '../../../api/client';
import { useOnboardingStore, consumePendingDeeplink } from '../store';
import type { CoachmarksState } from '../coachmarks';
import { Caveat } from '../../flow/atoms/Caveat';
import { PressBtn } from '../../../components/atoms/PressBtn';
import { Check } from '../../flow/atoms/Icons';
import { P, T } from '../../../styles/tokens';

const LOADER_STEPS = [
  'Сверяем твой город с зонами доставки…',
  'Проверяем кешбэк-кампании на сегодня…',
  'Отбираем находки до 1 000 ₽…',
  'Готовим первую витрину',
];

type StepState = 'pending' | 'active' | 'done' | 'error';

export function PreparingStep() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const platform = usePlatform();

  const { city, marketplaces, resetDraft } = useOnboardingStore();
  const [stepIdx, setStepIdx] = useState(0);
  const [failed, setFailed] = useState(false);
  const [inflight, setInflight] = useState(false);

  // Ref guard: prevents double-submit on StrictMode or re-render
  const submitted = useRef(false);

  // Disable back during PATCH; allow back on failure
  useEffect(() => {
    if (!failed) {
      // no-op back while loading
      platform.showBackButton(() => { /* intentionally disabled during flight */ });
    } else {
      platform.showBackButton(() => navigate('../city'));
    }
    return () => { platform.hideBackButton(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failed]);

  // Fake loader ticker — advances while API call is in flight
  useEffect(() => {
    if (failed) return;
    if (stepIdx >= LOADER_STEPS.length) return;
    const delay = stepIdx < LOADER_STEPS.length - 1 ? 900 : 600;
    const t = setTimeout(() => {
      setStepIdx((p) => Math.min(p + 1, LOADER_STEPS.length));
    }, delay);
    return () => clearTimeout(t);
  }, [stepIdx, failed]);

  async function submit() {
    if (submitted.current) return;
    submitted.current = true;
    setInflight(true);
    setFailed(false);
    setStepIdx(0);
    try {
      await apiFetch('/api/v2/buyer/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          city: city,
          marketplaces: marketplaces,
          onboarding_completed: true,
        }),
      });
      // Invalidate profile cache so home reads fresh data
      await queryClient.invalidateQueries({ queryKey: ['buyer', 'profile'] });
      // BUG-1132: update module cache so FirstLaunchGate won't redirect back to /onboarding.
      setOnboardingCompleted(true);
      resetDraft();
      // BUG-1165: restore pending deep-link stashed by DeepLinkHandler before
      // FirstLaunchGate redirected to /onboarding. Deep-link target takes priority
      // over the FTR-1114 coachmarks tour (tour only for organic first-launch).
      const pendingTarget = consumePendingDeeplink();
      if (pendingTarget) {
        navigate(pendingTarget, { replace: true });
      } else {
        // FTR-1114: hand the new buyer to HomeScreen with a one-time coachmarks tour.
        const coachState: CoachmarksState = { showCoachmarks: true };
        navigate('/', { replace: true, state: coachState });
      }
    } catch {
      submitted.current = false;
      setFailed(true);
      setInflight(false);
    }
  }

  // Run on mount
  useEffect(() => {
    submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getStepState(i: number): StepState {
    if (failed && i === LOADER_STEPS.length - 1) return 'error';
    if (i < stepIdx) return 'done';
    if (i === stepIdx) return 'active';
    return 'pending';
  }

  function handleRetry() {
    submit();
  }

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: '0 32px',
      textAlign: 'center', background: P.bg }}>

      {/* Spinner */}
      <div style={{
        width: 80, height: 80, borderRadius: 40,
        border: `5px solid rgba(226,114,91,0.18)`,
        borderTopColor: failed ? P.danger : P.terracotta,
        animation: inflight || (!failed) ? 'obSpin 1s linear infinite' : 'none',
      }} />

      <div style={{ marginTop: 22 }}>
        <Caveat color={P.terraDark} rot={-4} size={22}>момент</Caveat>
      </div>
      <div style={{ fontFamily: T.fontDisplay, fontSize: 22, fontWeight: 700,
        color: P.ink, marginTop: 8, lineHeight: 1.2 }}>
        {failed ? 'Что-то пошло не так' : 'Подбираем находки'}
      </div>

      {/* Step list */}
      <div style={{ marginTop: 20, width: '100%', maxWidth: 280,
        display: 'flex', flexDirection: 'column', gap: 8 }}>
        {LOADER_STEPS.map((label, i) => {
          const state = getStepState(i);
          const isErr = state === 'error';
          const isDone = state === 'done';
          const isActive = state === 'active';
          return (
            <div key={i} style={{
              display: 'flex', gap: 10, alignItems: 'center',
              opacity: state === 'pending' ? 0.35 : 1,
              transition: 'opacity 0.3s ease',
            }}>
              <div style={{
                width: 18, height: 18, borderRadius: 9,
                background: isErr ? P.danger : isDone ? P.success : isActive ? P.terracotta : 'rgba(25,23,23,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, color: '#fff',
              }}>
                {isDone && <Check size={10} />}
                {isErr && <span style={{ fontSize: 10, fontWeight: 700 }}>!</span>}
                {isActive && !isErr && (
                  <span style={{ fontSize: 9, fontWeight: 700 }}>{i + 1}</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: P.ink, textAlign: 'left' }}>
                {isErr ? 'ошибка' : label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Retry CTA */}
      {failed && (
        <div style={{ marginTop: 28, width: '100%', maxWidth: 280 }}>
          <PressBtn onClick={handleRetry} bg={P.terracotta}>
            Повторить
          </PressBtn>
        </div>
      )}

      <style>{`
        @keyframes obSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
